import { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Clock, Tag, Search } from "lucide-react";
import "./HomePage.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const BlogIndex = () => {
  const { slug } = useParams();
  const loc = useLocation();
  const filterKind = loc.pathname.includes("/category/") ? "category" : loc.pathname.includes("/tag/") ? "tag" : null;

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [search, setSearch] = useState("");
  const [searchActive, setSearchActive] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filterKind === "category" && slug) params.category = slug;
    if (filterKind === "tag" && slug) params.tag = slug;
    if (searchActive && search) params.q = search;
    axios.get(`${API}/public/blog`, { params })
      .then((r) => { setPosts(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [filterKind, slug, searchActive, search]);

  useEffect(() => {
    axios.get(`${API}/public/blog-categories`).then((r) => setCategories(r.data)).catch(() => {});
    axios.get(`${API}/public/blog-tags`).then((r) => setTags(r.data)).catch(() => {});
  }, []);

  const currentCategory = filterKind === "category" ? categories.find((c) => c.slug === slug) : null;
  const currentTag = filterKind === "tag" ? tags.find((t) => t.slug === slug) : null;

  const pageTitle = currentCategory
    ? currentCategory.name
    : currentTag
      ? `#${currentTag.name}`
      : "Insights from the content trenches.";

  const pageEyebrow = currentCategory ? "Category" : currentTag ? "Tag" : "Blog";

  return (
    <div className="prepify-v2">
      <nav className="nav-pill">
        <div className="nav-pill-inner">
          <Link to="/" className="nav-logo"><span className="nav-logo-dot" />prepify</Link>
          <div className="nav-links">
            <Link to="/#services"><button>Services</button></Link>
            <Link to="/#journey"><button>Journey</button></Link>
            <Link to="/blog"><button>Blog</button></Link>
            <Link to="/#pricing"><button>Pricing</button></Link>
          </div>
          <div className="nav-actions">
            <Link to="/login" className="nav-login">Staff Login</Link>
            <Link to="/#contact" className="btn-pill-primary nav-cta">Partner with us</Link>
          </div>
        </div>
      </nav>

      <section style={{ padding: "140px 24px 40px", background: "linear-gradient(180deg,#fff 0%,var(--mint-50) 100%)" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
          <span className="eyebrow"><span className="eyebrow-line" />{pageEyebrow}</span>
          <h1 className="sec-title" style={{ fontSize: "clamp(2.5rem, 6vw, 4.5rem)", marginTop: 16 }}>
            {currentCategory || currentTag ? pageTitle : <>Insights from the <em>content trenches.</em></>}
          </h1>
          {!currentCategory && !currentTag && (
            <p className="sec-desc" style={{ marginTop: 20 }}>
              Updates, guides, and behind-the-scenes from the team building India's coaching content engine.
            </p>
          )}
          {currentCategory?.description && <p className="sec-desc" style={{ marginTop: 16 }}>{currentCategory.description}</p>}
        </div>
      </section>

      {/* Filters / Search bar */}
      {!filterKind && (
        <section style={{ padding: "0 24px" }}>
          <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", padding: "16px 0" }}>
            <form
              onSubmit={(e) => { e.preventDefault(); setSearchActive(!!search); }}
              style={{ position: "relative", flex: "0 1 320px", minWidth: 220 }}
            >
              <Search size={14} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--ink-4)" }} />
              <input
                placeholder="Search articles…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); if (!e.target.value) setSearchActive(false); }}
                style={{ width: "100%", border: "1px solid var(--line)", borderRadius: 999, padding: "10px 16px 10px 36px", fontSize: 14, background: "#fff" }}
                data-testid="public-blog-search"
              />
            </form>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {categories.slice(0, 8).map((c) => (
                <Link key={c.id} to={`/blog/category/${c.slug}`} className="chip" style={{ color: "#fff", background: c.color || "#0057D9", borderColor: "transparent" }}>{c.name}</Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="sec" style={{ paddingTop: 20 }}>
        <div className="sec-inner">
          {(filterKind) && (
            <div style={{ marginBottom: 24 }}>
              <Link to="/blog" style={{ color: "var(--mint-700)", fontSize: 14, fontWeight: 500 }}>← All posts</Link>
            </div>
          )}
          {loading ? (
            <div style={{ textAlign: "center", color: "var(--ink-4)" }}>Loading…</div>
          ) : posts.length === 0 ? (
            <div style={{ textAlign: "center", color: "var(--ink-4)", padding: "60px 0" }}>
              <p>No articles found. {filterKind ? "Try another filter." : "Check back soon."}</p>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(340px,1fr))", gap: "24px" }}>
              {posts.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} className="service-v2" style={{ textDecoration: "none", color: "inherit", display: "block" }}>
                  {p.cover_image_url && (
                    <div style={{ margin: "-32px -32px 20px", aspectRatio: "16/10", overflow: "hidden", borderTopLeftRadius: 28, borderTopRightRadius: 28 }}>
                      <img src={p.cover_image_url.startsWith("/") ? `${BASE}${p.cover_image_url}` : p.cover_image_url} alt={p.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 12, color: "var(--ink-4)", marginBottom: 12, flexWrap: "wrap" }}>
                    {(p.category_ids || []).slice(0, 1).map((cid) => {
                      const c = categories.find((x) => x.id === cid);
                      return c ? <span key={cid} className="chip" style={{ background: c.color, color: "#fff", borderColor: "transparent" }}>{c.name}</span> : null;
                    })}
                    {p.category && (p.category_ids || []).length === 0 && <span className="chip">{p.category}</span>}
                    {p.published_at && <><Clock size={12} /> {new Date(p.published_at).toLocaleDateString()}</>}
                  </div>
                  <h3 className="service-v2-title" style={{ fontSize: "1.5rem", marginBottom: 12 }}>{p.title}</h3>
                  <p className="service-v2-desc" style={{ marginBottom: 16 }}>{p.excerpt}</p>
                  <div className="service-v2-pills">
                    {(p.tag_ids || []).slice(0, 3).map((tid) => {
                      const t = tags.find((x) => x.id === tid);
                      return t ? <span key={tid} className="chip"><Tag size={10} /> {t.name}</span> : null;
                    })}
                    {(p.tag_ids || []).length === 0 && (p.tags || []).slice(0, 3).map((t) => <span key={t} className="chip"><Tag size={10} /> {t}</span>)}
                  </div>
                  <div className="service-v2-arrow"><ArrowRight size={18} /></div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogIndex;
